import { AppRootQuery } from '../relay/app';
import { CartPageQuery } from '../relay/cart';
import { CheckoutPageQuery } from '../relay/checkout';
import { CollectionPageQuery } from '../relay/collection';
import { EmptyStateQuery } from '../relay/emptyState';
import { LoginPageQuery } from '../relay/login';
import { LogoutPageQuery } from '../relay/logout';
import { OrdersQuery } from '../relay/order';
import { AccountPageQuery } from '../relay/profile';
import { ProjectsQuery, SharedProjectPageQuery } from '../relay/project';
import {
  prepareVariablesForCollectionPage,
  prepareVariablesForDiscoverPage,
  prepareVariablesForOrdersPage,
  prepareVariablesForProjectsPage,
  renderLoadableRoute,
} from '../utils/router';

import { makeRouteConfig, Redirect, Route } from 'found';
import * as React from 'react';

// tslint:disable-next-line:no-submodule-imports
// import * as hotRouteConfig from 'found/lib/hotRouteConfig';

import PageNotFound from '../components/pages/404';
import ForgottentPasswordPage from '../components/pages/account/ForgottenPassword';
import Login, { LoginProps } from '../components/pages/account/Login';
import Logout from '../components/pages/account/Logout';
import OrderPage from '../components/pages/account/Orders';
import ProfilePage from '../components/pages/account/Profile';
import AccountRegistrationPage from '../components/pages/account/Registration';
import VerifyEmailPage from '../components/pages/account/VerifyEmail';
import BrowsePage from '../components/pages/browse/Browse';
import DiscoverPage from '../components/pages/browse/Discover';
import CartPage from '../components/pages/Cart';
import CheckoutPage from '../components/pages/Checkout';
import CheckoutCompletePage from '../components/pages/CheckoutComplete';
import CollectionPage from '../components/pages/Collection';
import FAQPage from '../components/pages/FAQ';
import NewsletterSuccessPage from '../components/pages/newsletter/Success';
import ProjectsPage from '../components/pages/Projects';
import SharedProjectsPage from '../components/pages/SharedProject';
import SectionTemplate from '../components/templates/Section';
import AppWithRelay from '../containers/App';
import { ArtistPageRoute } from './routes/ArtistPage';
import { ResultsPageRoute } from './routes/ResultsPage';
import { TrackPageRoute } from './routes/TrackPage';

// tslint:disable-next-line:prefer-const
let routeConfig = makeRouteConfig((
  <Route path="/">
    <Route
      path="login"
      title="Log In to Bopper"
      Component={Login}
      query={LoginPageQuery}
    />
    <Route
      path="signup"
      title="Sign Up for Bopper"
      Component={Login}
      query={LoginPageQuery}
      render={renderLoadableRoute<Pick<LoginProps, 'action'>>({ extraProps: { action: 'signup' } })}
    />
    <Route
      path="logout"
      title="Logging you out..."
      Component={Logout}
      query={LogoutPageQuery}
    />
    <Route
      Component={AppWithRelay}
      query={AppRootQuery}
    >
      <Redirect from="/" to={'/browse' + location.search} />
      {/* BACKWARD COMPAT: old site had a separate "discover" page, so we need to handle it
       even though now the "browse" page fulfills that role */}
      <Redirect from="/discover" to={'/browse' + location.search} />
      <Route
        key="discover-route"
        path="discover"
        Component={DiscoverPage}
        query={EmptyStateQuery}
        prepareVariables={prepareVariablesForDiscoverPage}
        render={renderLoadableRoute({ extraProps: { withCarousel: true } })}
      />
      <Route path="browse" Component={BrowsePage}>
        {ResultsPageRoute}
      </Route>
      {ArtistPageRoute}
      {TrackPageRoute}
      <Route
        path="collection/:slug"
        Component={CollectionPage}
        query={CollectionPageQuery}
        prepareVariables={prepareVariablesForCollectionPage}
        render={renderLoadableRoute()}
      />
      <Route
        path="my"
        Component={SectionTemplate}
      >
        <Route path="profile" Component={ProfilePage} query={AccountPageQuery} render={renderLoadableRoute()} />
        <Route path="playlists">
          <Route
            Component={ProjectsPage}
            query={ProjectsQuery}
            prepareVariables={prepareVariablesForProjectsPage}
            render={renderLoadableRoute()}
          />
          <Route
            path=":id"
            Component={ProjectsPage}
            query={ProjectsQuery}
            prepareVariables={prepareVariablesForProjectsPage}
          />
        </Route>
        {/* Need to keep existing project paths working even if they're now playlists */}
        <Route path="projects">
          <Route
            Component={ProjectsPage}
            query={ProjectsQuery}
            prepareVariables={prepareVariablesForProjectsPage}
            render={renderLoadableRoute()}
          />
          <Route
            path=":id"
            Component={ProjectsPage}
            query={ProjectsQuery}
            prepareVariables={prepareVariablesForProjectsPage}
          />
        </Route>
        <Route path="orders">
          <Route
            Component={OrderPage}
            query={OrdersQuery}
            prepareVariables={prepareVariablesForOrdersPage}
            render={renderLoadableRoute()}
          />
          <Route
            path=":invoice_number"
            Component={OrderPage}
            query={OrdersQuery}
            prepareVariables={prepareVariablesForOrdersPage}
          />
        </Route>
        <Route
          path="cart"
          title="Cart"
          Component={CartPage}
          query={CartPageQuery}
          render={renderLoadableRoute()}
        />
        <Route
          path="checkout/complete"
          title="Checkout Complete"
          Component={CheckoutCompletePage}
        />
        <Route
          path="checkout"
          title="Checkout"
          Component={CheckoutPage}
          query={CheckoutPageQuery}
          render={renderLoadableRoute()}
        />
      </Route>
      <Route path="/playlist/:opaque_id">
        <Route
          Component={SharedProjectsPage}
          query={SharedProjectPageQuery}
          render={renderLoadableRoute()}
        />
      </Route>
      <Route path="/project/:opaque_id">
        <Route
          Component={SharedProjectsPage}
          query={SharedProjectPageQuery}
          render={renderLoadableRoute()}
        />
      </Route>
      <Route
        path="/help"
        Component={SectionTemplate}
      >
        <Route
          path="faq/:id"
          title="FAQ Category"
          Component={FAQPage}
        />
        <Route
          path="faq"
          title="FAQ"
          Component={FAQPage}
        />
      </Route>
      <Route
        path="registration"
        title="Complete your registration"
        Component={AccountRegistrationPage}
        query={AccountPageQuery}
      />
      <Route
        path="forgotten-password"
        title="Forgot your password?"
        Component={ForgottentPasswordPage}
      />
      <Route
        path="verify"
        title="Verifying your email..."
        Component={VerifyEmailPage}
      />
      <Route
        path="newsletter/success"
        Component={NewsletterSuccessPage}
        query={EmptyStateQuery}
        render={renderLoadableRoute()}
      />
      <Route path="404" Component={PageNotFound} />
      <Redirect from="*" to="/404" />
    </Route>
  </Route>
));

// if (process.env.NODE_ENV !== 'production') {
//   routeConfig = hotRouteConfig(routeConfig);
// }

export default routeConfig;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/router/RouteConfig.tsx